import { apiHelper } from '@/utils'
import { API } from '@/constants'

/**
 * The agent desk.
 *
 * No tenant id anywhere in these paths. Scoping happens server-side from the
 * logged-in agent (App\Support\TenantContext), so a conversation belonging to
 * another brand simply 404s — there is nothing for this layer to pass, and
 * nothing it could pass to widen its own access.
 */
export const inboxService = {
  getConversations,
  getConversation,
  getMember,
  getLeads,
  pushLeadToOneSales,
  addLeadToOneSales,
  getMemory,
  forgetMemory,
  deleteMessage,
  labelTone,
  pushToMember,
  reply,
  sendAttachment,
  assign,
  setStatus,
  markRead,
  getAgents,
  createAgent,
  updateAgent,
}

const API_URL = API.BASE_URL + '/v1'

/**
 * @param {{status?: string, mine?: boolean, q?: string, pageNumber?: number, pageSize?: number}} filter
 *
 * Unfiltered is the work queue: the server sorts `waiting` first, oldest wait at
 * the top. Do not re-sort by recency in the UI — that buries the player who has
 * been waiting twenty minutes under chatter the bot is already handling.
 */
export async function getConversations(filter) {
  return apiHelper(API_URL).get('/conversations', {
    params: {
      status: filter?.status || undefined,
      mine: filter?.mine ? 1 : undefined,
      // Matches the player's name, their member id, or something said in the
      // conversation — the three things an agent has to hand.
      q: filter?.q || undefined,
      page: filter?.pageNumber,
      per_page: filter?.pageSize,
    },
  })
}

/**
 * Opening a thread marks it read for THIS agent only (per-agent watermark).
 *
 * `before` pages backwards through a long conversation and deliberately does
 * NOT mark anything read — the agent is reading history, not new work.
 */
export async function getConversation(uuid, before) {
  return apiHelper(API_URL).get(`/conversations/${uuid}`, {
    params: before ? { before } : {},
  })
}

/**
 * Send. `isPrivate` writes an internal note — saved, never delivered to the
 * player, and the only kind of message that does not take the conversation.
 */
export async function reply(uuid, body, isPrivate = false) {
  return apiHelper(API_URL).post(`/conversations/${uuid}/messages`, {
    body,
    is_private: isPrivate,
  })
}

export async function assign(uuid, userId) {
  return apiHelper(API_URL).patch(`/conversations/${uuid}/assignee`, { user_id: userId })
}

/** 'resolved' hands the conversation back to the bot. */
export async function setStatus(uuid, status) {
  return apiHelper(API_URL).patch(`/conversations/${uuid}/status`, { status })
}

export async function markRead(uuid) {
  return apiHelper(API_URL).post(`/conversations/${uuid}/read`)
}

/**
 * A file — in practice a screenshot of a transfer going back to the player.
 *
 * Sent as multipart, and the server decides the content type from the file
 * itself rather than trusting what the browser claims.
 */
export async function sendAttachment(uuid, file, body) {
  const form = new FormData()
  form.append('file', file)
  if (body) form.append('body', body)
  return apiHelper(API_URL).post(`/conversations/${uuid}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

/**
 * Who this conversation can be handed to: this brand's agents, plus us.
 *
 * `all` includes deactivated people — the team screen needs them, the
 * assignment dropdown must not offer them.
 */
export async function getAgents(all = false) {
  return apiHelper(API_URL).get('/agents', { params: all ? { all: 1 } : {} })
}

/**
 * Create an agent.
 *
 * `tenant_id` is only honoured for platform staff. A supervisor's agents land
 * in their own brand whatever they send, so the field is simply omitted from
 * their form rather than sent and ignored.
 */
export async function createAgent(payload) {
  return apiHelper(API_URL).post('/agents', payload)
}

/** Rename, promote, deactivate, unlock, or set a new password. */
export async function updateAgent(id, payload) {
  return apiHelper(API_URL).patch(`/agents/${id}`, payload)
}

/**
 * The member behind a conversation, from the brand's own backend.
 *
 * On demand: nothing is fetched until an agent presses the button, and the
 * answer is not cached, so what they see is what the account says now. The id
 * comes from the conversation on the server side — this takes no member id,
 * because an endpoint that looks up whatever it is given is a member directory.
 */
export async function getMember(uuid) {
  return apiHelper(API_URL).get(`/conversations/${uuid}/member`)
}

/** Briefs (leads) this conversation produced, newest first, with proposal status. */
export async function getLeads(uuid) {
  return apiHelper(API_URL).get(`/conversations/${uuid}/leads`)
}

/** "Add to One Sales" for a brief the bot did not push (or that failed). */
export async function pushLeadToOneSales(uuid, leadRef) {
  return apiHelper(API_URL).post(`/conversations/${uuid}/leads/${leadRef}/one-sales`, {})
}

/** File a lead by hand (name + phone/email) under this conversation, then push it. */
export async function addLeadToOneSales(uuid, data) {
  return apiHelper(API_URL).post(`/conversations/${uuid}/leads/manual`, data)
}

/** What the bot remembers about this customer across conversations. */
export async function getMemory(uuid) {
  return apiHelper(API_URL).get(`/conversations/${uuid}/memory`)
}

/** Soft-delete one message; the server decides who may. */
/** The desk's correction of the bot's tone verdict on one customer message. */
export async function labelTone(uuid, messageUuid, label) {
  return apiHelper(API_URL).post(`/conversations/${uuid}/messages/${messageUuid}/tone-label`, { label })
}

/** A message to a member who has not opened the chat. The brand goes in the
 *  header as well as the body: on "All brands" nothing else names it. */
export async function pushToMember(tenantId, memberId, body) {
  return apiHelper(API_URL).post(
    '/conversations/push',
    { tenant_id: tenantId, member_id: memberId, body },
    { headers: { 'X-Tenant-Id': String(tenantId) } },
  )
}

export async function deleteMessage(uuid, messageUuid) {
  return apiHelper(API_URL).delete(`/conversations/${uuid}/messages/${messageUuid}`)
}

/** PDPA "forget me" — wipes the bot's memory of this customer. */
export async function forgetMemory(uuid) {
  return apiHelper(API_URL).delete(`/conversations/${uuid}/memory`)
}
